import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain';
import { IUserRevisionService } from '@intigral/domain/lib/application/services/UserRevisionService';
import { useInterval } from '@intigral/hooks';
import { isFreeTier, isLegacyTier } from '@intigral/utils/lib/user';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import h from '../../../helpers/common';
import { IStore } from '../../../interfaces/StoreTypes';
import { fetchUserRevision } from '../../../store/reducers/userRevisionSlice';
import { getUserInfo, isKidsProfile } from '../../../utils/user';
import { REDEEM_QUEST_TYPE, USER_REVISION_CHECK_REFRESH } from '../constants';
import { EVENT } from '../constants/mixpanel';
import { HandleQuestClickProps } from '../types/iGamification';

const useRedeemActions = (balance, getNotifications) => {
    // Hooks & Contexts
    const { track } = useContext(MixpanelContext);
    const dispatch = useDispatch();
    const currentRevision = useSelector((state: IStore) => state.revision);

    // User Info
    const userInfo = getUserInfo();

    // Local States
    const [openQRModal, setOpenQRModal] = useState<boolean>(false);
    const [qrRewardType, setQrRewardType] = useState<string | null>(null);
    const [isUpgradeModalOpen, setUpgradeModalOpen] = useState<boolean>(false);
    const [isInsufficientPopup, setInsufficientPopup] = useState<boolean>(false);
    const [isInsufficientPopupType, setIsInsufficientPopupType] = useState<any>('');
    const [requiredPoint, setRequiredPoints] = useState(null);

    // Handlers
    const handleRewardClick = ({ points, rewardType, insufficientType, onSuccess }: HandleQuestClickProps) => {
        setRequiredPoints(points);
        if (isKidsProfile()) return;

        if (balance > points) {
            const rewardLabel = rewardType === 'TVOD' ? 'TVOD Rent' : 'Tier Upgrade';
            track(EVENT.GAMIFICATION.REDEEM_REWARD_CLICKED, {
                'Reward Type': rewardLabel,
            });
            track(EVENT.GAMIFICATION.REDEEM_REWARD_INITIATED, {
                Platform: 'Web',
                'Reward Type': REDEEM_QUEST_TYPE[rewardType],
                'Redemption Method': 'QR Code',
            });
            setQrRewardType(REDEEM_QUEST_TYPE[rewardType]);
            onSuccess();
            dispatch(fetchUserRevision());
        } else {
            setIsInsufficientPopupType(insufficientType);
            setInsufficientPopup(true);
        }
    };

    const handleClickMoviesStore = (points: number) =>
        handleRewardClick({
            points,
            rewardType: 'TVOD',
            insufficientType: 'movie_store',
            onSuccess: () => setOpenQRModal(true),
        });

    const handleClickTierUpgrade = (points: number) =>
        handleRewardClick({
            points,
            rewardType: 'UPGRADE',
            insufficientType: 'tier_upgrade',
            onSuccess: () => setUpgradeModalOpen(true),
        });

    const upgradeHandler = (isClick: boolean) => {
        setUpgradeModalOpen(false);
        if (isClick) {
            setQrRewardType(REDEEM_QUEST_TYPE.UPGRADE);
            setOpenQRModal(true);
            dispatch(fetchUserRevision());
        }
    };

    const insufficientPointPopupHandler = () => {
        setInsufficientPopup(false);
    };

    const showUpgradeRedeemQuest = () => {
        const tierGuid = (userInfo?.tierStatus?.tierGuid || userInfo?.tierConfig?.tierGuid || '')?.toUpperCase();
        if (isFreeTier(userInfo)) {
            return true;
        } else if (isLegacyTier(tierGuid)) {
            return true;
        }
        return false;
    };

    const pollUserRevision = useCallback(async () => {
        const userRevisionService = container.resolve<IUserRevisionService>(DI.SERVICES.IUserRevisionService);
        const accountId = h.getAccountId();
        const bucketId = h.getBucketIdFromAccountId(accountId);

        const results = await userRevisionService.getUserRevision(bucketId);
        const userRevision = results?.[accountId];

        if (userRevision?.subRev > currentRevision?.subRev) {
            getNotifications();
        }
    }, [currentRevision, getNotifications]);

    const { start, stop } = useInterval(pollUserRevision, USER_REVISION_CHECK_REFRESH);

    return {
        openQRModal,
        qrRewardType,
        isUpgradeModalOpen,
        isInsufficientPopup,
        isInsufficientPopupType,
        requiredPoint,
        setOpenQRModal,
        handleClickMoviesStore,
        handleClickTierUpgrade,
        upgradeHandler,
        insufficientPointPopupHandler,
        start,
        stop,
        showUpgradeRedeemQuest,
    };
};

export default useRedeemActions;
